import React, {useEffect, useState} from 'react';
import {
    List,
    TextField,
    FunctionField,
    Pagination,
    downloadCSV
} from 'react-admin';
import {parse} from 'json2csv';
import i18next from 'i18next';

import CustomDatagrid from '../components/CustomDatagrid';
import ProjectFilter from '../components/ProjectFilter';
import PublicationBadge from '~components/PublicationBadge';

import ListPageLayout from "../components/ListPageLayout";
import {useParams} from "react-router";
import {getRouteProjects, Route} from "~services/RoutesService";
import {RA_LIST_CUSTOM_STYLE} from "~screens/Dashboard/screens/ProjectsIIRSA/constants";
import {useSyncStoreToLocation} from "../hooks/useSyncStoreToLocation";
import {stripHtmlTags} from "~utils/textFormatting";
import {getDate} from "~utils/date";
import {CustomEmpty} from "~screens/Dashboard/screens/ProjectsIIRSA/components/CustomEmpty";

const names = (items?: {name: string}[]) => (items || []).map(item => item.name).join(', ');
// The stage stays on one line, as in the XD.
const NO_WRAP = {whiteSpace: 'nowrap'} as const;
// Shares of the width, so short values (country codes, stage, term) don't take room the long,
// many-valued ones need and rows stay shorter. The table's automatic layout keeps each column
// at least as wide as its header and unbreakable words, so nothing overflows on narrow screens.
const COLUMN_WIDTHS = {
    '& .column-name': {width: '27%'},
    '& .column-countries': {width: '8%'},
    '& .column-territories': {width: '16%'},
    '& .column-stage': {width: '10%'},
    '& .column-themes': {width: '11%'},
    '& .column-subthemes': {width: '19%'},
    '& .column-term': {width: '6%'},
    '& .column-publicationStatus': {width: '3%'},
};


// eslint-disable-next-line react/no-multi-comp
const PostPagination = props => {
    return <Pagination rowsPerPageOptions={[25, 50, 100]}  {...props} />;
};

function formatProject(p) {
    const t = (key: string) => i18next.t(`projects-iirsa:${key}`) as string;
    p[t('code')] = p.id;
    p[t('name')] = p.name;
    p[t('exportGoal')] = stripHtmlTags(p.goal);
    p[t('description')] = stripHtmlTags(p.description);
    p[t('countries')] = p.countriesFormatted;
    p[t('territories')] = names(p.territories);
    p[t('exportScope')] = names(p.scopes);
    p[t('theme')] = names(p.themes);
    p[t('subtheme')] = names(p.subthemes);
    p[t('exportProjectType')] = names(p.types);
    p[t('exportSource')] = p.source?.name;
    p[t('exportResponsibles')] = (p.responsibles || []).join(', ');
    p[t('stage')] = p.stage?.name;
    p[t('exportTerm')] = p.term?.name;
    p[t('exportEndDate')] = p.endDate ? getDate(p.endDate, 'MM/YYYY') : '';
}

const exporter = proj => {
    const t = (key: string) => i18next.t(`projects-iirsa:${key}`) as string;
    const page = proj?.page || proj || [];
    page.forEach(p => {
        formatProject(p);
    });
    const fields = [
        t('code'),
        t('name'),
        t('exportGoal'),
        t('description'),
        t('countries'),
        t('territories'),
        t('exportScope'),
        t('theme'),
        t('subtheme'),
        t('exportProjectType'),
        t('exportSource'),
        t('exportResponsibles'),
        t('stage'),
        t('exportTerm'),
        t('exportEndDate'),
    ];
    const formattedCsv = parse(proj, {fields});
    downloadCSV(formattedCsv, t('projects'));
};

// eslint-disable-next-line react/no-multi-comp
const ProjectList = (props) => {
        const { id } = useParams();

        useSyncStoreToLocation({
            storeKey: `project_list_filters_${id || 'all'}`,
            sort: {field: 'nombre', order: 'ASC'},
            perPage: 50,
        });

        const [state, setState] = useState<{
            route: Route | undefined;

        }>({
            route: undefined,
        });
        
        useEffect(() => {
            if (!id) {
                setState({...state, route: undefined});
                return;
            }

            getRouteProjects(id).then(route => {
                setState({...state, route: route});
            });
        }, [id]);
        

        const title = state?.route?.name || (i18next.t('projects-iirsa:projects') as string);
        // On a territory's page: "Inicio > Territorios > {nombre}".
        const breadcrumb = id ? [
            {name: i18next.t('projects-iirsa:start') as string, url: '/home'},
            {name: i18next.t('projects-iirsa:territories') as string, url: '/territories'},
            {name: title},
        ] : undefined;

        return (
            <ListPageLayout key={`project_custom_list_${id}`} mapPath="map" title={title} breadcrumb={breadcrumb}>
                <List
                    key={`project_list_${id}`}
                    {...props}
                    exporter={exporter}
                    storeKey={`project_list_filters_${id || 'all'}`}
                    filters={<ProjectFilter inTerritory={!!id}/>}
                    pagination={<PostPagination/>}
                    perPage={50}
                    disableSyncWithLocation={false}
                    // Only on a territory's page: a permanent `territory: undefined` would override the Territorio filter.
                    filter={id ? {territory: id} : undefined}
                    resource={'actions'}
                    sx={RA_LIST_CUSTOM_STYLE}
                    sort={{field: 'nombre', order: 'ASC'}}
                    emptyWhileLoading={false}
                    empty={<CustomEmpty/>}
                >
                    <CustomDatagrid rowClick="show" bulkActionButtons={false} sx={COLUMN_WIDTHS}>
                        <TextField source="name" label={i18next.t('projects-iirsa:name') as string} sortBy={'nombre'}/>
                        <FunctionField
                            source="countries"
                            label={i18next.t('projects-iirsa:countries') as string}
                            sortBy={'paises_string'}
                            render={(record: any) => (record?.countries || []).map((c: any) => c.code3 || c.code || c.name).join(', ')}
                        />
                        {/* Many-valued columns have no single value to sort by. */}
                        <FunctionField
                            source="territories"
                            sortable={false}
                            label={i18next.t('projects-iirsa:territories') as string}
                            render={(record: any) => names(record?.territories)}
                        />
                        <FunctionField
                            source="stage"
                            label={i18next.t('projects-iirsa:stage') as string}
                            sortBy={'etapa__nombre'}
                            sx={NO_WRAP}
                            render={(record: any) => record?.stage?.name}
                        />
                        <FunctionField source="themes" sortable={false} label={i18next.t('projects-iirsa:theme') as string} render={(record: any) => names(record?.themes)}/>
                        <FunctionField source="subthemes" sortable={false} label={i18next.t('projects-iirsa:subtheme') as string} render={(record: any) => names(record?.subthemes)}/>
                        <FunctionField
                            source="term"
                            label={i18next.t('projects-iirsa:exportTerm') as string}
                            sortBy={'plazo__nombre'}
                            render={(record: any) => record?.term?.name}
                        />
                        <FunctionField
                            source="publicationStatus"
                            sortable={false}
                            label=""
                            render={(record: any) => <PublicationBadge status={record?.publicationStatus}/>}
                        />
                    </CustomDatagrid>
                </List>
            </ListPageLayout>
        );
}

export default ProjectList;
